import React, { useCallback, useEffect, useMemo, useState, createRef } from "react";
import { Form, Formik } from "formik";
import groupBy from "lodash/groupBy";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import isEqual from "lodash/isEqual";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuery } from "@apollo/client/react";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import ItemCountBadge from "./ItemCountBadge";

import SingleDeliveryFields from "../../shopping/Cart/SingleDeliveryFields";
import { useCustomFields } from "../../../contexts/customFields";
import { validationSchema } from "../../shopping/Cart/CartForm";
import {
  UPDATE_INVENTORY_ITEM_MUTATION,
  UPDATE_INVENTORY_REQUEST_MUTATION,
} from "../../../constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import { usePermissions } from "../../../contexts/permissions";
import { getLocationNotes } from "../../../views/shopping/Cart/CartFormWizard";
import AddAsset from "../../../views/inventory/requests/AddAsset";
import LocationNotes from "../../../views/shopping/Cart/WizardCheckout/LocationNotes";
import { isStartDateGreaterEndDate } from "../../shopping/Cart/WizardCheckout/Step2";
import { isVaSubdomain } from "../../../utils/inventoryRequests";
import { getInventoryFields } from "../../../views/inventory/requests/CartItemFields";
import CartItems from "./CartItems";
import { FETCH_PROJECT_QUERY, INVENTORY_REQUEST_QUERY } from "../../../constants/graphql/queries";
import Attachments from "./Attachments";
import Header from "../header/Header";
import OutboundOrderGuidedWizardModal from "./orders/outbound-order-guided-wizard/OutboundOrderGuidedWizardModal";
import OutboundWeightBreakdownModal from "./OutboundWeightBreakdownModal";
import { useNavigate } from "react-router";
import Status from "../header/Status";
import useIsInventoryRequestDisabled from "../../../hooks/useIsInventoryRequestDisabled";
import useQueryParams from "../../../hooks/useQueryParams";
import { fullWidthDialog } from "src/constants/dialog";
import useRoles from "../../../hooks/user/useRoles";
import useUnsavedChanges from "../../../hooks/useUnsavedChanges";
import UnsavedChangesDialog from "../../../components/modals/UnsavedChanges/UnsavedChangesDialog";
import SerialNumbersEditorModal from "../../../components/form/SerialNumbersEditorModal";

const formikRef = createRef();

function calculateOutboundWeight(inventoryItems = []) {
  return inventoryItems.reduce((totalWeight, inventoryItem) => {
    const quantity = Number(inventoryItem?.quantity || 0);
    const assetWeight = Number(inventoryItem?.asset?.weight || 0);

    return totalWeight + quantity * assetWeight;
  }, 0);
}

function formatOutboundWeight(outboundWeight) {
  if (outboundWeight == null || outboundWeight === "") return "";

  return `${Number(outboundWeight).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} lbs`;
}

function parseManualOutboundWeight(value) {
  if (value == null || value === "") return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function weightsDiffer(a, b) {
  return Math.abs(Number(a || 0) - Number(b || 0)) >= 0.01;
}

const trackingDetailsToText = (trackingDetails = []) =>
  trackingDetails
    .map((entry) => entry?.tracking_number || entry?.trackingNumber || "")
    .filter(Boolean)
    .join("\n");

const textToTrackingDetails = (value = "") =>
  value
    .split(/\n|,/)
    .map((trackingNumber) => trackingNumber.trim())
    .filter(Boolean)
    .map((trackingNumber) => ({ tracking_number: trackingNumber }));

export default function InventoryRequestModal({
  id,
  updateInventoryRequest,
  changeAllInventoryItems,
  removeInventoryItem,
  updateInventoryItem,
  disabledVaStatus,
  onClose,
  viewOnly = false,
}) {
  const { user } = usePermissions();
  const navigate = useNavigate();
  const query = useQueryParams();
  const outboundOrderGuidedWizard = query.get("outboundOrderGuidedWizard");
  const showOutboundWeightOverride = query.get("outboundWeightOverride") === String(id);
  const showOutboundWeightBreakdown =
    query.get("outboundWeightBreakdown") === String(id) || showOutboundWeightOverride;
  const returnTo = query.get("returnTo");
  const [submitCounter, setSubmitCounter] = useState(0);
  const [localInventoryItemCount, setLocalInventoryItemCount] = useState(null);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const { notifySuccess, notifyError } = useNotification();
  const [updateItem] = useMutation(UPDATE_INVENTORY_ITEM_MUTATION);
  const [saveInventoryRequest, { loading: savingManualOutboundWeight }] = useMutation(
    UPDATE_INVENTORY_REQUEST_MUTATION
  );
  const emailIsRequired = !!user.accessLink?.id;
  const hasAssistantPermission = useRoles(["admin", "manager", "inventory"]);

  useEffect(() => {
    setLocalInventoryItemCount(null);
  }, [id]);

  const handleClose = useCallback(
    (...args) => {
      if (returnTo && returnTo.startsWith("/")) {
        return navigate(returnTo, { replace: true });
      }
      onClose(...args);
    },
    [navigate, returnTo, onClose]
  );

  const { attemptClose, confirmDiscard, cancelDiscard, confirmSave, showDialog, saving } =
    useUnsavedChanges({
      onClose: handleClose,
      isDirtyFn: () => !!formikRef.current?.dirty,
      onSave: async () => {
        const submitFn = formikRef.current?.submitForm;
        if (submitFn) submitFn();
      },
    });

  const {
    loading,
    data: { inventoryRequest = {} } = {},
    refetch,
  } = useQuery(INVENTORY_REQUEST_QUERY, { variables: { id } });
  const isInventoryRequestDisabled = useIsInventoryRequestDisabled(inventoryRequest);
  const effectiveDisabled = viewOnly || isInventoryRequestDisabled;
  const projectId =
    inventoryRequest?.projectId ||
    inventoryRequest?.inventoryItems?.map((v) => v?.project?.id).filter((v) => v)?.[0];
  const embeddedProjectSettings = inventoryRequest?.inventoryItems?.find((item) => item?.project?.settings)
    ?.project?.settings;
  const { data: { project: inventoryRequestProject = {} } = {} } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });
  const requiresOutboundTracking = !!(
    embeddedProjectSettings?.requires_outbound_tracking_for_completion ??
    inventoryRequestProject?.settings?.requires_outbound_tracking_for_completion
  );
  const [display] = useCustomFields({ projectId });
  const receivingWizard = user.company?.settings?.receiving_wizard;

  const locationNotes = Array.isArray(inventoryRequest.locationNotes)
    ? inventoryRequest.locationNotes
    : [];
  const wizardMode = user.company?.settings?.wizard_mode;
  const initialValues = useMemo(
    () => ({
      poNumber: inventoryRequest.poNumber,
      email: inventoryRequest.email,
      ccEmails: inventoryRequest.ccEmails,
      requirements: inventoryRequest.requirements,
      comment: inventoryRequest.comment || "",
      configurableField1: inventoryRequest.configurableField1 || "",
      configurableField2: inventoryRequest.configurableField2 || "",
      configurableField3: inventoryRequest.configurableField3 || "",
      configurableField4: inventoryRequest.configurableField4 || "",
      configurableField5: inventoryRequest.configurableField5 || "",
      designer: inventoryRequest.designer,
      startDate: inventoryRequest.startDate,
      startTime: inventoryRequest.startTime || (inventoryRequest.startDate ? "00:00:00" : ""),
      endDate: inventoryRequest.endDate,
      endTime: inventoryRequest.endTime,
      contactName: inventoryRequest.contactName,
      contactPhoneNumber: inventoryRequest.contactPhoneNumber,
      resources: inventoryRequest.resources || [],
      street: inventoryRequest.street || "",
      city: inventoryRequest.city || "",
      zipCode: inventoryRequest.zipCode || "",
      location: inventoryRequest.location || "",
      deliveryDate: inventoryRequest.deliveryDate || "",
      manualOutboundWeight:
        inventoryRequest.manualOutboundWeight == null ? "" : String(inventoryRequest.manualOutboundWeight),
      disposition: inventoryRequest.disposition || "",
      members: inventoryRequest.members || [],
      locationNotes: Object.fromEntries(locationNotes.map((v) => [v.location, v.note])),
      locationOrder: Object.fromEntries(locationNotes.map((v) => [v.location, v.position])),
      inventoryItems: inventoryRequest.inventoryItems || [],
      pickingNotes: inventoryRequest.pickingNotes || "",
      billingQuantities: inventoryRequest.billingQuantities || [],
      pickingStartTime: inventoryRequest.pickingStartTime || "",
      pickingCompleteTime: inventoryRequest.pickingCompleteTime || "",
      finalReleaseNotes: inventoryRequest.finalReleaseNotes || "",
      trackingDetails: inventoryRequest.trackingDetails || [],
    }),
    [inventoryRequest, locationNotes]
  );

  const handleUpdateInventoryRequest = useCallback(
    async ({ inventoryItems, manualOutboundWeight, ...values }) => {
      if (wizardMode && !inventoryItems.every((v) => v.location)) {
        setSubmitCounter((v) => v + 1);
        return notifyError("Location required");
      }

      if (isStartDateGreaterEndDate(values)) {
        return notifyError("End date must be after start date");
      }

      const { locationNotes, locationOrder = {}, ...input } = values;
      input.manualOutboundWeight = parseManualOutboundWeight(manualOutboundWeight);
      const locations = Object.entries(groupBy(inventoryItems, "location")).map(
        ([v]) => v.split(",")[0]
      );
      if (inventoryItems.length === 0) {
        input.locationNotes = Object.entries(locationNotes).map(([location, note]) => ({
          location,
          note,
        }));
      } else if (locationNotes) {
        locations.forEach((value) => {
          if (!locationNotes[value]) {
            locationNotes[value] = "";
          }
        });
        input.locationNotes = getLocationNotes(locationNotes, locationOrder).filter((v) =>
          locations.includes(v.location)
        );
      }

      await Promise.all(
        inventoryItems
          .filter((v, index) => !isEqual(v, (inventoryRequest.inventoryItems || [])[index]))
          .map((inventoryItem) => {
            const variables = {
              id: inventoryItem.id,
              ...getInventoryFields({
                ...inventoryItem,
                projectId: inventoryItem.project.id,
                assetId: inventoryItem.asset.id,
              }),
            };
            return updateItem({ variables });
          })
      );

      await saveInventoryRequest({
        variables: { id: inventoryRequest.id, input },
        onSuccess: ({ inventoryRequest: updatedInventoryRequest }) => {
          if (updateInventoryRequest) {
            updateInventoryRequest(inventoryRequest.id, updatedInventoryRequest);
          }
          notifySuccess("Order successfully updated");
        },
        onFailure: (errors) => {
          notifyError(errors.submit || errors);
          // Reload so a stale form picks up the server's current (e.g. locked) state.
          refetch();
        },
      });
    },
    [
      saveInventoryRequest,
      inventoryRequest,
      updateInventoryRequest,
      notifySuccess,
      notifyError,
      initialValues,
      refetch,
    ]
  );

  const changeStatus = useCallback(
    (key) => (checked) => {
      const input = {
        vaStatus: {
          [key]: checked,
        },
      };
      saveInventoryRequest({
        variables: { id: inventoryRequest.id, input },
        onSuccess: () => {
          refetch();
        },
      });
    },
    [refetch, inventoryRequest]
  );

  const isVA = isVaSubdomain(); // Check if it's a VA subdomain

  const handleAddAsset = useCallback((newItem) => {
    // Backend now handles setting change_order status automatically
    // when adding items to scheduled/picked/completed orders
    if (newItem) {
      return {
        ...newItem,
        pickedStatus: false,
      };
    }
    return newItem;
  }, []);

  const handleItemAdded = useCallback(
    (inventoryItem) => {
      if (!inventoryItem) return;

      const appendItem = (items = []) =>
        items.some(item => String(item.id) === String(inventoryItem.id))
          ? items
          : [...items, inventoryItem];

      const nextItems = appendItem(formikRef.current?.values?.inventoryItems || []);

      formikRef.current?.setFieldValue("inventoryItems", nextItems, false);
      setLocalInventoryItemCount(nextItems.length);
    },
    []
  );

  const openOutboundOrderAssistant = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("outboundOrderGuidedWizard", id);
    params.set("step", "details");
    navigate({ search: params.toString() });
  };

  const closeOutboundOrderAssistant = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("outboundOrderGuidedWizard");
    params.delete("step");
    navigate({ search: params.toString() });
  };

  const openOutboundWeightBreakdown = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("outboundWeightBreakdown", id);
    navigate({ search: params.toString() });
  };

  const closeOutboundWeightBreakdown = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("outboundWeightBreakdown");
    params.delete("outboundWeightOverride");
    navigate({ search: params.toString() });
  };

  const openOutboundWeightOverride = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("outboundWeightBreakdown", id);
    params.set("outboundWeightOverride", id);
    navigate({ search: params.toString() });
  };

  const closeOutboundWeightOverride = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("outboundWeightOverride");
    navigate({ search: params.toString() });
  };

  const handleSaveManualOutboundWeight = useCallback(
    async (manualOutboundWeight) => {
      const parsedManualOutboundWeight = parseManualOutboundWeight(manualOutboundWeight);
      const nextManualOutboundWeight =
        parsedManualOutboundWeight == null ? "" : String(parsedManualOutboundWeight);

      try {
        await saveInventoryRequest({
          variables: {
            id: inventoryRequest.id,
            input: {
              manualOutboundWeight: parsedManualOutboundWeight,
            },
          },
          onSuccess: ({ inventoryRequest: updatedInventoryRequest }) => {
            formikRef.current?.setFieldValue("manualOutboundWeight", nextManualOutboundWeight, false);
            if (updateInventoryRequest) {
              updateInventoryRequest(inventoryRequest.id, updatedInventoryRequest);
            }
            notifySuccess("Outbound weight updated");
            closeOutboundWeightOverride();
          },
        });
      } catch (error) {
        notifyError("Failed to update outbound weight");
      }
    },
    [
      saveInventoryRequest,
      inventoryRequest,
      updateInventoryRequest,
      notifySuccess,
      notifyError,
      closeOutboundWeightOverride,
    ]
  );

  const title = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={1}
      position="relative"
      width="100%"
    >
      <Typography color="textPrimary" fontSize={24} fontWeight={500}>
        {viewOnly
          ? isVA
            ? "View Task Order"
            : "View Order"
          : isVA
            ? "Edit Task Order"
            : "Edit Order"}
      </Typography>
      <ItemCountBadge count={localInventoryItemCount ?? inventoryRequest?.inventoryItems?.length ?? 0} />
      <IconButton
        onClick={attemptClose}
        size="small"
        sx={{ position: "absolute", right: 0, top: 0 }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );

  if (loading) {
    return (
      <Dialog
        open
        fullWidth
        maxWidth={false}
        onClose={attemptClose}
        aria-labelledby="dialog-title"
        PaperProps={{
          sx: {
            maxWidth: "1050px",
          },
        }}
        sx={fullWidthDialog}
      >
        <DialogTitle id="dialog-title">{title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {[...Array(14)].map((_, i) => (
              <Grid key={i} size={{ xs: 12, md: 6 }}>
                <Skeleton variant="rectangular" height={40} />
              </Grid>
            ))}
            {[...Array(3)].map((_, i) => (
              <Grid key={`skeleton-${i}`} size={12}>
                <Skeleton variant="rectangular" height={120} />
              </Grid>
            ))}
          </Grid>
          <Box mt={2} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      onClose={attemptClose}
      aria-labelledby="dialog-title"
      PaperProps={{
        sx: {
          maxWidth: "1050px",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
      sx={fullWidthDialog}
    >
      <Formik
        innerRef={formikRef}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema({
          email: emailIsRequired,
        })}
        onSubmit={handleUpdateInventoryRequest}
      >
        {({ isSubmitting, submitForm, values, setFieldValue }) => (
          <Form style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {(() => {
              const calculatedOutboundWeight = calculateOutboundWeight(values.inventoryItems);
              const persistedOutboundWeight = Number(inventoryRequest.outboundWeight || 0);
              const manualOutboundWeight = parseManualOutboundWeight(values.manualOutboundWeight);
              const billingOutboundWeight =
                manualOutboundWeight != null ? manualOutboundWeight : persistedOutboundWeight;
              const displayedOutboundWeight =
                billingOutboundWeight > 0 ? billingOutboundWeight : calculatedOutboundWeight;
              const showOutboundWeight = displayedOutboundWeight > 0;
              const outboundWeightHelperText = weightsDiffer(displayedOutboundWeight, calculatedOutboundWeight)
                ? `Click to view item-by-item breakdown. Current item total is ${formatOutboundWeight(calculatedOutboundWeight)}.`
                : "Click to view the item-by-item weight breakdown";

              return (
                <>
                  <DialogTitle id="dialog-title">{title}</DialogTitle>
                  <DialogContent sx={{ flexGrow: 1, overflowY: "auto" }}>
                    <Box mb={2}>
                      <Header item={inventoryRequest} />
                    </Box>
                    <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
                      <Status
                        loading={false}
                        disabled={disabledVaStatus || effectiveDisabled}
                        inventoryRequest={inventoryRequest}
                        changeStatus={changeStatus}
                      />
                    </Box>
                    <DialogContentText>
                      <SingleDeliveryFields
                        projectId={projectId}
                        inventoryRequest={inventoryRequest}
                        email={emailIsRequired}
                        workOrderNumber={inventoryRequest.workOrderNumber}
                        user={inventoryRequest.user}
                        revisionNumber={inventoryRequest.revisionNumber}
                        hideAddressDropdown={
                          inventoryRequest.street || inventoryRequest.city || inventoryRequest.zipCode
                        }
                        multipleDelivery={false}
                        setMultipleDelivery={() => null}
                        changeAllInventoryItems={changeAllInventoryItems}
                        fieldDisabled={effectiveDisabled}
                        showAllConfigurableFields={true} // Show fields 4 & 5 in edit modal
                        showPickingNotesWhenPresentOnly={true}
                        disabled={effectiveDisabled}
                        afterComment={
                          <>
                            {showOutboundWeight && (
                              <Grid size={12}>
                                <Box onClick={openOutboundWeightBreakdown}>
                                  <TextField
                                    label="Outbound Weight"
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    value={formatOutboundWeight(displayedOutboundWeight)}
                                    helperText={outboundWeightHelperText}
                                    InputProps={{
                                      readOnly: true,
                                    }}
                                    sx={{
                                      cursor: "pointer",
                                      "& .MuiInputBase-root": {
                                        cursor: "pointer",
                                      },
                                      "& .MuiInputBase-input": {
                                        cursor: "pointer",
                                      },
                                    }}
                                  />
                                </Box>
                              </Grid>
                            )}
                            <Grid size={12}>
                              <TextField
                                label="Release Notes"
                                fullWidth
                                multiline
                                size="small"
                                variant="outlined"
                                value={values.finalReleaseNotes}
                                onChange={(e) => setFieldValue("finalReleaseNotes", e.target.value)}
                                disabled={effectiveDisabled}
                              />
                            </Grid>
                            {requiresOutboundTracking && (
                              <Grid size={12}>
                                <TextField
                                  label="Tracking Numbers"
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  value={trackingDetailsToText(values.trackingDetails).split("\n").join(", ")}
                                  onClick={() => !effectiveDisabled && setTrackingModalOpen(true)}
                                  helperText="Click to add or edit tracking numbers"
                                  disabled={effectiveDisabled}
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  sx={{
                                    cursor: effectiveDisabled ? "default" : "pointer",
                                    "& .MuiInputBase-root": {
                                      cursor: effectiveDisabled ? "default" : "pointer",
                                    },
                                    "& .MuiInputBase-input": {
                                      cursor: effectiveDisabled ? "default" : "pointer",
                                    },
                                  }}
                                />
                              </Grid>
                            )}
                          </>
                        }
                      >
                        <Box>
                          <Box my={1.5}>
                            {wizardMode && values.inventoryItems.length === 0 && (
                              <LocationNotes list={[]} disabled={effectiveDisabled} />
                            )}
                            {values.inventoryItems.length > 0 && (
                              <CartItems
                                submitCounter={submitCounter}
                                multipleDelivery={false}
                                centerAligned={true}
                                display={{
                                  ...display,
                                  ii_location: false,
                                  ii_building: false,
                                  ii_floor: false,
                                  ii_room: false,
                                  ii_po_number: false,
                                  ii_idsr_number: false,
                                  ii_eoc_number: false,
                                  ii_deliveryDate: false,
                                  ii_deliveryTime: false,
                                }}
                                items={values.inventoryItems}
                                removeInventoryItem={removeInventoryItem}
                                updateInventoryItem={updateInventoryItem}
                                reFetch={refetch}
                                actions={
                                  !viewOnly &&
                                  hasAssistantPermission && (
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      onClick={openOutboundOrderAssistant}
                                    >
                                      Outbound Order Assistant
                                    </Button>
                                  )
                                }
                                locationNotesDisabled={effectiveDisabled}
                                disabled={effectiveDisabled}
                              />
                            )}

                            <Attachments
                              entityId={id}
                              entityType="inventoryRequest"
                              disabled={effectiveDisabled}
                            />

                            {!effectiveDisabled && (
                              <AddAsset
                                projectId={receivingWizard ? projectId : undefined}
                                values={initialValues}
                                refetch={refetch}
                                onItemAdded={handleItemAdded}
                                processNewItem={handleAddAsset}
                              />
                            )}
                          </Box>
                        </Box>
                      </SingleDeliveryFields>
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Box display="flex" justifyContent="flex-end" width="100%" p={2} pr={3}>
                      <Box display="flex" gap={1}>
                        <Button variant="outlined" onClick={attemptClose} color="primary">
                          Close
                        </Button>
                        {!effectiveDisabled && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={submitForm}
                            disabled={isSubmitting}
                            startIcon={isSubmitting && <CircularProgress size={22} />}
                          >
                            Save
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </DialogActions>
                  <OutboundWeightBreakdownModal
                    open={showOutboundWeightBreakdown}
                    onClose={closeOutboundWeightBreakdown}
                    inventoryItems={values.inventoryItems}
                    totalOutboundWeight={displayedOutboundWeight}
                    calculatedTotalOutboundWeight={calculatedOutboundWeight}
                    manualOutboundWeight={values.manualOutboundWeight}
                    onSaveManualOutboundWeight={handleSaveManualOutboundWeight}
                    overrideModalOpen={showOutboundWeightOverride}
                    onOpenOverrideModal={openOutboundWeightOverride}
                    onCloseOverrideModal={closeOutboundWeightOverride}
                    savingManualOutboundWeight={savingManualOutboundWeight}
                    disabled={effectiveDisabled}
                  />
                  <SerialNumbersEditorModal
                    open={trackingModalOpen}
                    onClose={() => setTrackingModalOpen(false)}
                    title="Tracking Numbers"
                    fieldLabel="Tracking Number"
                    addButtonLabel="+ Add Tracking Number"
                    scanTooltip="Scan Tracking Number"
                    initialSerials={trackingDetailsToText(values.trackingDetails).split("\n").filter(Boolean)}
                    onSave={async (trackingNumbers) => {
                      const trackingDetails = textToTrackingDetails(trackingNumbers.join("\n"));
                      let saved = false;
                      await saveInventoryRequest({
                        variables: {
                          id: inventoryRequest.id,
                          input: { trackingDetails },
                        },
                        onSuccess: ({ inventoryRequest: updatedInventoryRequest }) => {
                          saved = true;
                          setFieldValue("trackingDetails", updatedInventoryRequest.trackingDetails || trackingDetails);
                          if (updateInventoryRequest) {
                            updateInventoryRequest(inventoryRequest.id, updatedInventoryRequest);
                          }
                          notifySuccess("Tracking numbers updated");
                        },
                        onFailure: notifyError,
                      });
                      if (!saved) throw new Error("Failed to update tracking numbers");
                    }}
                  />
                </>
              );
            })()}
          </Form>
        )}
      </Formik>
      {showDialog && (
        <UnsavedChangesDialog
          open={showDialog}
          onCancel={cancelDiscard}
          onDiscard={confirmDiscard}
          onSave={confirmSave}
          saving={saving}
        />
      )}
      {outboundOrderGuidedWizard && (
        <OutboundOrderGuidedWizardModal
          inventoryRequest={inventoryRequest}
          onToggle={closeOutboundOrderAssistant}
        />
      )}
    </Dialog>
  );
}
